import { defineComponent as _defineComponent } from 'vue'
import { ActionProps } from '@/components/atoms/action/Action.vue';
import { PaginatorProps } from '@/components/molecules/paginator/Paginator.vue';
import { TableHeaderProps } from '@/components/molecules/table/header/TableHeader.vue';
import { ISortConfig } from '@/components/molecules/table/header/types';
import { TableRowProps } from '@/components/molecules/table/row/TableRow.vue';
import ListPage from '@/components/template/list/ListPage.vue';
import Status from '@/enum/Status';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { IRequest } from '@/services/api/models';
import RequestService from '@/services/api/RequestService';
import { getCurrentInstance, onMounted, reactive } from "vue";
import { DEFAULT_LIMIT, } from './shared/config/constants';
import getFilterFields, { INITIAL_FILTERS } from './shared/config/filter';
import { IEditableRequestsFilters, IRequestsFilters } from './shared/config/filter/types';
import getTableConfig, { DEFAULT_SORT } from './shared/config/table';
import { RequestColumnKeys } from './shared/config/table/types';
import { formatMassiveUpload, normalizeControlZoneId } from './shared/utils';
import { RequestListData, RequestListState } from './types';

export interface RequestListProps {
  title: string;
  subtitle: string;
  showExportButton?: boolean;
  columns: RequestColumnKeys[];
  filters?: {
    initial: IRequestsFilters;
    mainSearcher?: string | null;
    fields: (keyof IEditableRequestsFilters)[];
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RequestsList',
  props: {
    title: null,
    subtitle: null,
    showExportButton: { type: Boolean },
    columns: null,
    filters: null
  },
  setup(__props: any) {

const props = __props as {
  title: string;
  subtitle: string;
  showExportButton?: boolean;
  columns: RequestColumnKeys[];
  filters?: {
    initial: IRequestsFilters;
    mainSearcher?: string | null;
    fields: (keyof IEditableRequestsFilters)[];
  }
};

const instance = getCurrentInstance();
const proxy = instance && instance.proxy as any;


const data = reactive<RequestListData>({
  dictionary: {} as any,
  requests: [] as IRequest[],
  companies: [] as any[],
  subscriptors: [] as any[],
  branches: [] as any[],
  statuses: {} as any,
  massiveUploads: [] as any[],
  trimesters: [] as string[],
});

const state = reactive<RequestListState>({
  filters: { ...INITIAL_FILTERS, companyId: proxy.$store.state.companyId } as IRequestsFilters,
  currentRequest: 0,
  tableConfig: {} as any,
  limit: DEFAULT_LIMIT,
  page: 1,
  totalRows: 0,
  currentSort: { ...DEFAULT_SORT },
  isLoading: false,
});

const requestService = new RequestService(proxy);

//----------------------------
const loadDictionary = async () => {
  const response = await requestService.getDictionary();
  data.dictionary = response;
};

const loadCompanies = async () => {
  const response: any = await proxy.$root._post(API_ENDPOINTS.COMPANIES_ALL);
  data.companies = response.data;
};

const loadSubscriptors = async () => {
  const response: any = await proxy.$root._post(API_ENDPOINTS.SUBSCRIPTORS_ALL);
  data.subscriptors = response.data;
};

const loadBranches = async () => {
  const response: any = await proxy.$root._get(API_ENDPOINTS.BRANCHES_ALL);
  data.branches = response.data;
};

const loadTrimesters = async () => {
  const response: any = await proxy.$root._get(API_ENDPOINTS.REQUESTS_TRIMESTERS);
  data.trimesters = response;
};

const loadStatuses = async () => {
  const response: any = await proxy.$root._get(API_ENDPOINTS.REQUESTS_STATUS);
  data.statuses = response;
};

const loadMassiveUploads = async () => {
  const response: any = await proxy.$root._post(
    `companies/${proxy.$store.state.companyId}/${API_ENDPOINTS.MASSIVE_UPLOADS}`,
    {
      status: 2,
      order: {
        col: "title",
        dir: "asc"
      }
    }
  );
  data.massiveUploads = response.data.map(formatMassiveUpload);
};

const loadRequests = async (page: number, sort: ISortConfig, showPopup?: boolean) => {
  state.isLoading = true;
  state.currentSort = sort;
  state.filters.controlZoneId = normalizeControlZoneId(state.filters.controlZoneId);

  try {
    const { totalRows, data: requests } = await requestService.getRequests({
      limit: state.limit,
      page,
      order: state.currentSort,
      filters: {
        ...state.filters,
        status: state.filters.status ? state.filters.status : props.filters && props.filters.initial ? props.filters.initial.status : undefined,
      },
    })
    state.totalRows = totalRows;
    data.requests = requests;

    if (showPopup) {
      proxy.$root.notify('Busqueda realizada con éxito', 'success');
    }
  } finally {
    state.isLoading = false;
  }
};

const loadTableConfig = () => {
  state.tableConfig = getTableConfig({
    statuses: data.statuses,
    requestsService: requestService,
  });
}
//----------------------------

const handleSearch = async (page: number, sort: ISortConfig) => {
  await loadRequests(page, sort, false);
};

const handleUpdateFilters = (updatedFilters: Record<string, any>) => {
  Object.keys(updatedFilters).forEach(key => {
    state.filters[key] = updatedFilters[key];
  });
};

const clearFilters = () => {
  Object.keys(state.filters).forEach((key: string) => {
    if (key !== 'modifyType' && key !== 'companyId') {
      state.filters[key] = '';
    }
  });
  handleSearch(1, state.currentSort);
};

const getValidStatusesForFilter = (): Record<Status, string> => {
  if (props.filters && props.filters.initial && props.filters.initial.status) {
    const { status } = props.filters.initial;
    if (Array.isArray(status)) {
      return status.reduce((acc, key) => ({
        ...acc,
        [key]: data.statuses[key]
      }), {} as Record<Status, string>);
    }
    return {
      [status]: data.statuses[status],
    } as Record<Status, string>;
  }
  return data.statuses;
};

const getFilters = () => {
  const fields = getFilterFields({
    branches: data.branches,
    statuses: getValidStatusesForFilter(),
    trimesters: data.trimesters,
  }, props.filters && props.filters.fields);

  return {
    title: "Filtros",
    fields,
    mainSearcher: props.filters ? props.filters.mainSearcher : undefined,
    filters: state.filters,
    isLoading: state.isLoading,
    currentSort: state.currentSort,
    search: handleSearch,
    clearFilters: clearFilters,
    updateFilters: handleUpdateFilters,
  };
};

//----------------------------

const getTableHeader = (): TableHeaderProps => ({
  columns: props.columns.map((column) => state.tableConfig[column]),
  onSort: handleSearch,
  currentSort: state.currentSort,
});

const getTableBody = (): TableRowProps[] =>
  data.requests.map((request: IRequest) => {
    const cells = props.columns.map((column) => state.tableConfig[column].value(request));
    return { cells };
  });

const getTable = () => ({
  header: getTableHeader(),
  body: getTableBody(),
});

//----------------------------
const onPageChange = (page: number, limit: number) => {
  state.limit = limit;
  state.page = page;
  loadRequests(page, state.currentSort);
};

const getPaginator = (): PaginatorProps => ({
  currentPage: state.page,
  totalRows: state.totalRows,
  limit: state.limit,
  onPageChange: onPageChange,
});

//----------------------------

const exportData = async () => {
  try {
    const response: any = await (proxy.$root as any)._post('requests/bordereaux', {
      ...state.filters,
      status: state.filters.status ? state.filters.status : props.filters && props.filters.initial ? props.filters.initial.status : undefined,
    });
    let result = await (proxy.$root as any)._getFile(response.path);
    let fileName = response.path.split("/").slice(-1);
    const url = window.URL.createObjectURL(new Blob([result]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName); //or any other extension
    document.body.appendChild(link);
    link.click();
    link.remove();
    /*window.open(response.path);*/
  } catch (e) {
    (proxy.$root as any).notify(
      "No se pudo exportar, intente más tarde.",
      "error"
    );
  }
};

const getActions = () => {
  const exportButton: ActionProps = props.showExportButton ? {
    onClick: exportData,
    label: "Exportar bordereaux"
  } : null;

  return [exportButton].filter(Boolean);
}

//----------------------------

onMounted(async () => {
  state.isLoading = true;
  await Promise.all([
    loadDictionary(),
    loadCompanies(),
    loadSubscriptors(),
    loadBranches(),
    loadStatuses(),
    loadTrimesters(),
    loadMassiveUploads(),
  ]);
  await loadRequests(1, state.currentSort);
  loadTableConfig();
  state.isLoading = false;
});

return { __sfc: true,instance, proxy, props, data, state, requestService, loadDictionary, loadCompanies, loadSubscriptors, loadBranches, loadTrimesters, loadStatuses, loadMassiveUploads, loadRequests, loadTableConfig, handleSearch, handleUpdateFilters, clearFilters, getValidStatusesForFilter, getFilters, getTableHeader, getTableBody, getTable, onPageChange, getPaginator, exportData, getActions, ListPage }
}

})